/**
 * Diff-sync engine for the abstract floorplan editor.
 *
 * Compares the edited draft against the baseline (last server state) and
 * issues the minimal CRUD sequence against the nested floorplan routers.
 *
 * Ordering matters:
 *   1. delete flows (so endpoint deletes can't orphan survivors mid-run)
 *   2. delete stations/zones
 *   3. create stations/zones (recording tmp-cid → public_id mappings)
 *   4. create flows (endpoints resolved through the mapping)
 *   5. update changed stations/zones/flows
 */
import {
  createPlanFlow,
  createPlanStation,
  createPlanZone,
  deletePlanFlow,
  deletePlanStation,
  deletePlanZone,
  updatePlanFlow,
  updatePlanStation,
  updatePlanZone,
} from '../../../services/reunification.service';
import type {
  PlanFlowWrite,
  PlanStationWrite,
  PlanZoneWrite,
} from '../../../types/ReunificationTypes';
import type {
  DraftFlow,
  DraftStation,
  DraftZone,
  FloorplanDraft,
  FlowEndpoint,
} from './types';

interface SyncArgs {
  templateId: string;
  floorplanId: string;
  baseline: FloorplanDraft;
  draft: FloorplanDraft;
}

const STATION_FIELDS = [
  'name',
  'description',
  'station_type',
  'canvas_x',
  'canvas_y',
  'min_staff',
  'recommended_staff',
  'staff_ratio_per_students',
  'capacity',
  'requires_equipment',
  'is_required',
  'sort_order',
] as const;

const ZONE_FIELDS = [
  'name',
  'description',
  'zone_type',
  'canvas_x',
  'canvas_y',
  'canvas_width',
  'canvas_height',
  'capacity',
  'color',
] as const;

const FLOW_FIELDS = [
  'flow_type',
  'description',
  'is_primary_path',
  'sequence_order',
] as const;

function changedFields<T extends object>(
  before: T,
  after: T,
  fields: readonly (keyof T)[],
): Partial<T> {
  const patch: Partial<T> = {};
  for (const field of fields) {
    if (JSON.stringify(before[field]) !== JSON.stringify(after[field])) {
      patch[field] = after[field];
    }
  }
  return patch;
}

function stationWrite(station: DraftStation): PlanStationWrite {
  return {
    name: station.name,
    description: station.description,
    station_type: station.station_type,
    canvas_x: station.canvas_x,
    canvas_y: station.canvas_y,
    min_staff: station.min_staff,
    recommended_staff: station.recommended_staff,
    staff_ratio_per_students: station.staff_ratio_per_students,
    capacity: station.capacity,
    requires_equipment: station.requires_equipment,
    is_required: station.is_required,
    sort_order: station.sort_order,
  };
}

function zoneWrite(zone: DraftZone): PlanZoneWrite {
  return {
    name: zone.name,
    description: zone.description,
    zone_type: zone.zone_type,
    canvas_x: zone.canvas_x,
    canvas_y: zone.canvas_y,
    canvas_width: zone.canvas_width,
    canvas_height: zone.canvas_height,
    capacity: zone.capacity,
    color: zone.color,
  };
}

function flowEndpoints(
  flow: DraftFlow,
  resolve: (endpoint: FlowEndpoint) => string,
): Pick<
  PlanFlowWrite,
  'from_station' | 'from_zone' | 'to_station' | 'to_zone'
> {
  return {
    from_station: flow.from.kind === 'station' ? resolve(flow.from) : null,
    from_zone: flow.from.kind === 'zone' ? resolve(flow.from) : null,
    to_station: flow.to.kind === 'station' ? resolve(flow.to) : null,
    to_zone: flow.to.kind === 'zone' ? resolve(flow.to) : null,
  };
}

function endpointsEqual(a: DraftFlow, b: DraftFlow): boolean {
  return (
    a.from.kind === b.from.kind &&
    a.from.cid === b.from.cid &&
    a.to.kind === b.to.kind &&
    a.to.cid === b.to.cid
  );
}

/**
 * Sync the draft to the server. Returns the number of write operations
 * performed (useful for toasts/tests).
 */
export async function syncFloorplanDraft({
  templateId,
  floorplanId,
  baseline,
  draft,
}: SyncArgs): Promise<number> {
  let writes = 0;
  // tmp-cid → server public_id; pre-seeded with identity for server entities.
  const cidToPublicId = new Map<string, string>();
  for (const entity of [...draft.stations, ...draft.zones]) {
    if (entity.publicId) cidToPublicId.set(entity.cid, entity.publicId);
  }
  const resolve = (endpoint: FlowEndpoint): string => {
    const publicId = cidToPublicId.get(endpoint.cid);
    if (!publicId) {
      throw new Error(
        `Unresolved floorplan endpoint: ${endpoint.kind} ${endpoint.cid}`,
      );
    }
    return publicId;
  };

  const draftFlowIds = new Set(
    draft.flows.map((f) => f.publicId).filter(Boolean),
  );
  const draftStationIds = new Set(
    draft.stations.map((s) => s.publicId).filter(Boolean),
  );
  const draftZoneIds = new Set(
    draft.zones.map((z) => z.publicId).filter(Boolean),
  );

  // 1. flow deletes
  for (const flow of baseline.flows) {
    if (flow.publicId && !draftFlowIds.has(flow.publicId)) {
      await deletePlanFlow(templateId, floorplanId, flow.publicId);
      writes += 1;
    }
  }

  // 2. station/zone deletes (backend cascades their remaining flows; the
  //    draft cascade in useFloorplanDraft mirrors this client-side)
  for (const station of baseline.stations) {
    if (station.publicId && !draftStationIds.has(station.publicId)) {
      await deletePlanStation(templateId, floorplanId, station.publicId);
      writes += 1;
    }
  }
  for (const zone of baseline.zones) {
    if (zone.publicId && !draftZoneIds.has(zone.publicId)) {
      await deletePlanZone(templateId, floorplanId, zone.publicId);
      writes += 1;
    }
  }

  // 3. station/zone creates
  for (const station of draft.stations) {
    if (!station.publicId) {
      const created = await createPlanStation(
        templateId,
        floorplanId,
        stationWrite(station),
      );
      cidToPublicId.set(station.cid, created.public_id);
      writes += 1;
    }
  }
  for (const zone of draft.zones) {
    if (!zone.publicId) {
      const created = await createPlanZone(
        templateId,
        floorplanId,
        zoneWrite(zone),
      );
      cidToPublicId.set(zone.cid, created.public_id);
      writes += 1;
    }
  }

  // 4. flow creates
  for (const flow of draft.flows) {
    if (!flow.publicId) {
      await createPlanFlow(templateId, floorplanId, {
        flow_type: flow.flow_type,
        description: flow.description,
        is_primary_path: flow.is_primary_path,
        sequence_order: flow.sequence_order,
        ...flowEndpoints(flow, resolve),
      });
      writes += 1;
    }
  }

  // 5. updates
  const baselineStations = new Map(
    baseline.stations
      .filter((s) => s.publicId)
      .map((s) => [s.publicId as string, s]),
  );
  for (const station of draft.stations) {
    const before = station.publicId
      ? baselineStations.get(station.publicId)
      : undefined;
    if (!before) continue;
    const patch = changedFields(before, station, STATION_FIELDS);
    if (Object.keys(patch).length > 0) {
      await updatePlanStation(
        templateId,
        floorplanId,
        station.publicId as string,
        patch,
      );
      writes += 1;
    }
  }

  const baselineZones = new Map(
    baseline.zones
      .filter((z) => z.publicId)
      .map((z) => [z.publicId as string, z]),
  );
  for (const zone of draft.zones) {
    const before = zone.publicId ? baselineZones.get(zone.publicId) : undefined;
    if (!before) continue;
    const patch = changedFields(before, zone, ZONE_FIELDS);
    if (Object.keys(patch).length > 0) {
      await updatePlanZone(
        templateId,
        floorplanId,
        zone.publicId as string,
        patch,
      );
      writes += 1;
    }
  }

  const baselineFlows = new Map(
    baseline.flows
      .filter((f) => f.publicId)
      .map((f) => [f.publicId as string, f]),
  );
  for (const flow of draft.flows) {
    const before = flow.publicId ? baselineFlows.get(flow.publicId) : undefined;
    if (!before) continue;
    const patch: Partial<PlanFlowWrite> = changedFields(
      before,
      flow,
      FLOW_FIELDS,
    );
    if (!endpointsEqual(before, flow)) {
      Object.assign(patch, flowEndpoints(flow, resolve));
    }
    if (Object.keys(patch).length > 0) {
      await updatePlanFlow(
        templateId,
        floorplanId,
        flow.publicId as string,
        patch,
      );
      writes += 1;
    }
  }

  return writes;
}
